import React, { useEffect, useState } from 'react';
import TitlePage from './pages/TitlePage';
import ResultsOverviewPage from './pages/ResultsOverviewPage';
import ResultsDetailPage from './pages/ResultsDetailPage';
import Loader from './components/Loader';
import ErrorPage from './components/ErrorPage';
import { LanguageContext } from './components/Localized';
import { LastPage } from './components/LastPage';
// import testData from './data'; <<-- for local development
import metaProcurementExcellence from './assets/metaProcEx.json';
import metaComplexity from './assets/metaComplexity.json';
import metaDataScience from './assets/metaDataScience.json';
import metaDigitalMaturity from './assets/metaDigitalMaturity.json';
import metaService from './assets/metaService.json';
import metaIT from './assets/metaIT.json';
import metaSmartManufacturing from './assets/metaSmartManufacturing.json';
import metaServicePricing from './assets/metaServicePricing.json';
import metaPlanning from './assets/metaPlanning.json';
import metaDataScienceAndAI from './assets/metaDataScienceAndAI.json';

// make sure that the new assessment is included in the meta data assessment array below
const metaDataAssessments = [
    metaProcurementExcellence,
    metaDigitalMaturity,
    metaSmartManufacturing,
    metaIT,
    metaService,
    metaDataScience,
    metaComplexity,
    metaServicePricing,
    metaPlanning,
    metaDataScienceAndAI,
];

/**
 * @brief main entrypoint for the react application
 */
const App = () => {
    const [error, setError] = useState(null);
    const [params, setParams] = useState(null);

    // validate and set query params
    useEffect(() => {
        const queryString = parse_query_string(window.location.search.substring(1));
        // validate if the query params are available in the right format, if not set an error
        if (queryString.sid === undefined || queryString.token === undefined) {
            setError('no-access-data');
        } else if (!queryString.token) {
            setError('no-token');
        } else {
            const meta = get_survey_metadata(parseInt(queryString.sid, 10));
            if (!meta) {
                setError('no-metadata');
            } else {
                // set the query params
                setParams({
                    sid: parseInt(queryString.sid, 10),
                    token: queryString.token.toString().replace(/\s+/g, ''),
                    lang:
                        queryString.lang !== undefined && meta.languages.includes(queryString.lang)
                            ? queryString.lang
                            : meta.languages[0],
                });
            }
        }
    }, []);

    if (error !== null) {
        return <ErrorPage error={error} />;
    }

    if (params === null) {
        return <Loader />;
    }

    const meta = get_survey_metadata(parseInt(params.sid, 10));
    return (
        <LanguageContext.Provider value={params.lang}>
            <Main sid={params.sid} token={params.token} assessmentMetaData={meta} />
        </LanguageContext.Provider>
    );
};

/**
 * @brief the main component that renders the pdf based on the url params provided by the App component
 * @param {number} sid
 * @param {string} token
 * @param {object} assessmentMetaData
 */
const Main = ({ sid, token, assessmentMetaData }) => {
    /* --------------------------------- Initialise states --------------------------------- */
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [assessmentData, setAssessmentData] = useState(null);

    /* ----------------------------- Load Survey Results from API ----------------------------- */
    // save URI-params as FormDate to have to correct format to send to API
    const formData = new FormData();
    if (error === null) {
        formData.append('sid', sid);
        formData.append('token', token);
    }

    // make API-call after rest of page has loaded
    useEffect(() => {
        // only send API-request when page is set to "loading"
        if (!isLoaded) {
            /*
            //this is for local development
            setTimeout(() => {
                setAssessmentData(testData);
                setIsLoaded(true);
            }, 1000);
             */

            fetch('https://assessment.zellweger.com/assessment-api/data.php', {
                body: formData,
                method: 'POST',
            })
                .then((res) => res.json())
                .then(
                    (result) => {
                        try {
                            if (result.status === 'ok') {
                                setAssessmentData(result);
                                setIsLoaded(true);
                            } else {
                                setError('server-auth-error');
                            }
                        } catch (e) {
                            setError('server-auth-error');
                        }
                    },
                    (error) => {
                        setError('server-generic-error');
                    }
                );
        }
    }, [isLoaded]);

    // Automatically print page after report is rendered.
    useEffect(() => {
        if (isLoaded) {
            window.setTimeout(window.print, 600);
        }
    }, [isLoaded]);

    /* ---------------------------------- Render Component ----------------------------------- */
    if (error !== null) {
        return <ErrorPage error={error} />;
    }

    if (isLoaded) {
        /* Prepare the data to render the detail pages */
        const iterateArray = [...Array(assessmentMetaData.bestPracticeInfo.length).keys()];

        let questionRangeArray = [];
        let start = 0;
        // define for each detail page that the start id of the question answer array is and how many entries should be considered (length)
        for (let category of assessmentData.data) {
            questionRangeArray.push({
                start: start,
                length: category.subcatArray.length,
            });
            start += category.subcatArray.length;
        }

        return (
            <div>
                <TitlePage assessmentName={assessmentMetaData.assessmentName} />
                <ResultsOverviewPage
                    assessmentData={assessmentData}
                    overviewTitle={assessmentMetaData.overviewTitle}
                    overviewInfo={assessmentMetaData.overviewInfo}
                />
                {iterateArray.map((index) => (
                    <ResultsDetailPage
                        key={index.toString()}
                        index={index}
                        assessmentData={assessmentData}
                        excellenceModel={assessmentMetaData.excellenceModel}
                        bestPracticeText={assessmentMetaData.bestPracticeInfo[index]}
                        questionRange={questionRangeArray[index]}
                    />
                ))}
                <LastPage sid={sid} />
            </div>
        );
    }

    return <Loader />;
};

/**
 * @brief This helper function splits the query string from the user URI-input in two variables.
 * @param {String} query URI-String e.g. 'sid=983565&token=R8POtZznzGMGISQ'
 */
function parse_query_string(query) {
    var vars = query.split('&');
    var query_string = {};
    for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split('=');
        var key = decodeURIComponent(pair[0]);
        var value = decodeURIComponent(pair[1]);
        // If first entry with this name
        if (typeof query_string[key] === 'undefined') {
            query_string[key] = decodeURIComponent(value);
            // If second entry with this name
        } else if (typeof query_string[key] === 'string') {
            var arr = [query_string[key], decodeURIComponent(value)];
            query_string[key] = arr;
            // If third or later entry with this name
        } else {
            query_string[key].push(decodeURIComponent(value));
        }
    }
    return query_string;
}

/**
 * @brief This helper function gets the survey metadata if available (returns an json-object)
 * @param {number} sid
 */
function get_survey_metadata(sid) {
    const meta = metaDataAssessments.filter((metaData) => metaData.sid === sid);
    if (meta.length === 1) {
        return meta[0];
    }
    return null;
}

export default App;
